export type CitySlug =
  | 'ottawa'
  | 'kingston'
  | 'perth'
  | 'brockville'
  | 'carleton-place'
  | 'peterborough';

export type City = {
  slug: CitySlug;
  name: string;
  region: 'Ontario';
  /** Used in copy (e.g. "drivers in the Limestone City"). */
  nickname?: string;
  /** Latitude / longitude — used for the search CTA `near` query and JSON-LD. */
  lat: number;
  lng: number;
  /** Approximate population, for the JSON-LD City schema. */
  population: number;
  /** One-line subhead under the H1. */
  tagline: string;
  /** Neighbourhoods listed in copy + JSON-LD areaServed. */
  neighbourhoods: string[];
  /** Common use cases — render as bullet list. */
  useCases: string[];
  /** Trailer-type slugs that are popular in this city. Match TYPE_GRID slugs on Home. */
  popularTypes: Array<{
    slug: string;
    label: string;
    emoji: string;
    example: string;
  }>;
  /** FAQ entries — also emitted as FAQPage JSON-LD for AI assistants. */
  faqs: Array<{ q: string; a: string }>;
  /** Why TowShare beats U-Haul in this specific market. */
  whyHere: string[];
};

const SHARED_FAQS = (cityName: string) => [
  {
    q: `Is TowShare cheaper than U-Haul in ${cityName}?`,
    a: `Almost always, yes. Because TowShare trailers are owned by your neighbours and not by a national rental chain, daily rates are typically 30–50% lower than U-Haul or a yard rental in ${cityName} — and you save the driving time to and from the rental yard, because pickup is usually within a few kilometres of you.`,
  },
  {
    q: `Do I need special insurance to rent a trailer in ${cityName}?`,
    a: `Most Ontario auto policies extend liability and collision to a trailer you're towing — check your policy or call your broker. Every TowShare booking is also backed by our $1,500 Trailer Guarantee, which protects owners against damage.`,
  },
  {
    q: `How quickly can I get a trailer in ${cityName}?`,
    a: `Many ${cityName}-area owners accept same-day bookings if you book before noon. The search map at /search shows everyone within 10 km of your pickup point so you can pick the closest available trailer.`,
  },
  {
    q: `Can I list my own trailer in ${cityName}?`,
    a: `Yes — listing is free. Most ${cityName} owners earn $200–$600 per month renting out a trailer that would otherwise sit in their driveway. TowShare handles ID verification, payments, payouts, and the $1,500 damage guarantee. You set the price and the calendar.`,
  },
];

export const CITIES: Record<CitySlug, City> = {
  ottawa: {
    slug: 'ottawa',
    name: 'Ottawa',
    region: 'Ontario',
    nickname: 'the National Capital',
    lat: 45.4215,
    lng: -75.6972,
    population: 1017449,
    tagline:
      'Skip the U-Haul lineup on St. Laurent. Rent a utility, dump, or car-hauler trailer from a verified neighbour — usually within 10 minutes of where you live.',
    neighbourhoods: [
      'Kanata',
      'Stittsville',
      'Barrhaven',
      'Nepean',
      'Orléans',
      'Westboro',
      'Hintonburg',
      'The Glebe',
      'Vanier',
      'Alta Vista',
      'Riverside South',
      'Manotick',
      'Greely',
      'Gloucester',
    ],
    useCases: [
      'Trail Road landfill dump runs',
      'IKEA Pinecrest pickups',
      'Apartment moves between Centretown and the suburbs',
      'ATV and dirt-bike transport up to Calabogie or the Madawaska',
      'Renovation debris hauls after a kitchen or bathroom reno',
      'Snowmobile transport north to Edelweiss or Mont Cascades',
    ],
    popularTypes: [
      { slug: 'utility', label: 'Utility', emoji: '🛻', example: 'Dump runs & moves' },
      { slug: 'dump', label: 'Dump', emoji: '🪨', example: 'Renos & landscaping' },
      { slug: 'enclosed', label: 'Enclosed', emoji: '📦', example: 'Furniture & gear' },
      { slug: 'car_hauler', label: 'Car hauler', emoji: '🚗', example: 'Project cars & ATVs' },
    ],
    whyHere: [
      'Ottawa is TowShare\'s largest market — there are more verified trailers within 10 km of you than any other Canadian city.',
      'No more 45-minute drives across the Queensway to a rental yard — neighbourhood pickups across Kanata, Orléans, and Barrhaven.',
      'Owners in suburbs like Stittsville, Manotick, and Greely typically have larger driveways and bigger trailers (16′+ dumps, 20′ car haulers).',
    ],
    faqs: [
      {
        q: 'Where can I rent a dump trailer in Ottawa?',
        a: 'Dozens of dump trailers are listed across Ottawa — most concentrated in Kanata, Stittsville, Barrhaven, Manotick, and Orléans where homeowners use them for landscaping. Filter by /search?type=dump and your postal code to see what\'s available within 10 km.',
      },
      {
        q: 'Can I rent a car hauler in Ottawa to move a project car?',
        a: 'Yes — TowShare car haulers in Ottawa typically range from 18′ to 22′ with electric brakes and a GVWR around 7,000–10,000 lbs. Common pickup areas are Kanata, Nepean, Stittsville, and Greely. Filter by /search?type=car_hauler.',
      },
      ...SHARED_FAQS('Ottawa'),
    ],
  },

  kingston: {
    slug: 'kingston',
    name: 'Kingston',
    region: 'Ontario',
    nickname: 'the Limestone City',
    lat: 44.2312,
    lng: -76.4860,
    population: 132485,
    tagline:
      'Trailers for moving day at Queen\'s, hauling a boat to the Thousand Islands, or running debris from a Sydenham renovation — booked in 60 seconds.',
    neighbourhoods: [
      'Downtown Kingston',
      'Williamsville',
      'Sydenham',
      'Reddendale',
      'Cataraqui',
      'Amherstview',
      'Bath',
      'Wolfe Island',
      'Inner Harbour',
      'Kingston East',
      'Westwoods',
    ],
    useCases: [
      'Queen\'s University and St. Lawrence College move-in and move-out weekends',
      'Boat haul-outs and launches in the Thousand Islands',
      'Snowmobile transport up Highway 38 toward Frontenac',
      'Cottage furniture moves to and from Sharbot Lake',
      'Motorcycle transport for Ontario riders heading to Calabogie Motorsports Park',
    ],
    popularTypes: [
      { slug: 'utility', label: 'Utility', emoji: '🛻', example: 'Student moves & hauls' },
      { slug: 'enclosed', label: 'Enclosed', emoji: '📦', example: 'Weatherproof gear' },
      { slug: 'snowmobile', label: 'Snowmobile', emoji: '❄️', example: 'Sleds to Frontenac' },
      { slug: 'motorcycle', label: 'Motorcycle', emoji: '🏍️', example: 'Bikes to Calabogie' },
    ],
    whyHere: [
      'Kingston has the highest concentration of student moves in Eastern Ontario — TowShare owners run 3× more rentals during May and September move weekends.',
      'Lakefront access means high demand for boat, snowmobile, and motorcycle trailers — rarer at chain rental yards.',
      'Wolfe Island and Amherstview owners often list larger landscape and equipment trailers used on rural properties.',
    ],
    faqs: [
      {
        q: 'Can I rent a trailer in Kingston for Queen\'s move-out?',
        a: 'Yes — May 1 and early September are TowShare\'s busiest Kingston weekends. Book 2–3 weeks ahead to lock in a utility or enclosed trailer at a Williamsville, Sydenham, or downtown pickup point. Hourly rentals are common during student moves.',
      },
      {
        q: 'Where can I find a snowmobile trailer in Kingston?',
        a: 'Snowmobile trailers cluster in Kingston East, Amherstview, and the Cataraqui area. Filter by /search?type=snowmobile — single and double-sled trailers are available November through March.',
      },
      ...SHARED_FAQS('Kingston'),
    ],
  },

  perth: {
    slug: 'perth',
    name: 'Perth',
    region: 'Ontario',
    nickname: 'the heritage town',
    lat: 44.8995,
    lng: -76.2486,
    population: 6500,
    tagline:
      'Landscape, dump, and ag-equipment trailers across Lanark County — from heritage Perth to the Rideau Lakes and Tay Valley.',
    neighbourhoods: [
      'Downtown Perth',
      'Tay Valley',
      'Drummond/North Elmsley',
      'Lanark Highlands',
      'Rideau Lakes',
      'Smiths Falls',
      'Lanark Village',
      'Maberly',
      'Westport',
    ],
    useCases: [
      'Landscape and mulch deliveries across Lanark County',
      'Ag-equipment moves between farms in Drummond and Tay Valley',
      'Garage and barn cleanouts on heritage properties',
      'Cottage and lake-house material runs around Christie Lake and the Rideau system',
      'Maple-syrup and woodlot equipment transport',
    ],
    popularTypes: [
      { slug: 'landscape', label: 'Landscape', emoji: '🌿', example: 'Mulch & soil' },
      { slug: 'dump', label: 'Dump', emoji: '🪨', example: 'Cleanups & demo' },
      { slug: 'utility', label: 'Utility', emoji: '🛻', example: 'General hauls' },
      { slug: 'flatbed', label: 'Flatbed', emoji: '🏗️', example: 'Lumber & skids' },
    ],
    whyHere: [
      'Lanark County is one of Ontario\'s most under-served rental markets — the nearest U-Haul yard is 30–45 minutes away.',
      'TowShare owners in Tay Valley and Drummond often list heavy landscape and flatbed trailers used for working farms.',
      'Heritage Perth properties generate frequent renovation and cleanup hauls — a dump trailer is almost always available within 15 km of downtown.',
    ],
    faqs: [
      {
        q: 'Is there a U-Haul in Perth, ON?',
        a: 'The nearest large U-Haul yards are in Smiths Falls and Carleton Place — 25–40 minutes away. TowShare is the closer option for almost everyone in central Lanark County, with pickups in Perth, Lanark Village, and Tay Valley.',
      },
      {
        q: 'Can I rent a landscape trailer in Perth for spring cleanup?',
        a: 'Yes. Landscape and dump trailers are TowShare Perth\'s most popular categories from April through October. Many owners list with mulch sides already installed.',
      },
      ...SHARED_FAQS('Perth'),
    ],
  },

  brockville: {
    slug: 'brockville',
    name: 'Brockville',
    region: 'Ontario',
    nickname: 'the City of the 1000 Islands',
    lat: 44.5895,
    lng: -75.6840,
    population: 21858,
    tagline:
      'Rent car-hauler, utility, and enclosed trailers along the St. Lawrence — Brockville, Prescott, Mallorytown, and the 1000 Islands.',
    neighbourhoods: [
      'Downtown Brockville',
      'North Augusta',
      'Lyn',
      'Mallorytown',
      'Prescott',
      'Athens',
      'Maitland',
      'Cardinal',
      'Gananoque',
    ],
    useCases: [
      'Project-car moves between Ottawa, Brockville, and the U.S. border crossings',
      'Boat moves in and out of 1000 Islands marinas',
      'Residential moves along the Highway 401 corridor',
      'Renovation and dump runs on Brockville\'s waterfront homes',
      'ATV transport up to Smiths Falls and the Frontenac Provincial Park',
    ],
    popularTypes: [
      { slug: 'car_hauler', label: 'Car hauler', emoji: '🚗', example: 'Project cars' },
      { slug: 'utility', label: 'Utility', emoji: '🛻', example: 'General hauls' },
      { slug: 'enclosed', label: 'Enclosed', emoji: '📦', example: 'Cross-border gear' },
      { slug: 'dump', label: 'Dump', emoji: '🪨', example: 'Waterfront cleanups' },
    ],
    whyHere: [
      'Brockville sits between three major markets (Ottawa, Kingston, Montreal) — TowShare owners here often serve cross-corridor rentals.',
      '1000 Islands waterfront properties drive consistent demand for boat haulers and dump trailers.',
      'Lower density than Ottawa or Kingston means listings tend to be available on short notice, even on weekends.',
    ],
    faqs: [
      {
        q: 'Can I rent a car hauler in Brockville to cross the U.S. border?',
        a: 'Yes — TowShare doesn\'t restrict cross-border use, but renters are responsible for ensuring the trailer is properly registered and that any goods being transported comply with CBSA / CBP rules. Confirm with the owner before booking.',
      },
      {
        q: 'Where can I rent a boat trailer in Brockville?',
        a: 'Boat-capable trailers (single-axle utility and dedicated boat haulers) cluster in Brockville, Mallorytown, and Gananoque. Most are listed in /search?type=utility or /search?type=flatbed; filter by your launch site to find the closest.',
      },
      ...SHARED_FAQS('Brockville'),
    ],
  },

  'carleton-place': {
    slug: 'carleton-place',
    name: 'Carleton Place',
    region: 'Ontario',
    nickname: 'the gateway to the Ottawa Valley',
    lat: 45.1383,
    lng: -76.1419,
    population: 12517,
    tagline:
      'Trailer rentals on Highway 7 — 25 minutes from Kanata. Utility, enclosed, dump, and snowmobile trailers across Carleton Place, Almonte, and Beckwith.',
    neighbourhoods: [
      'Downtown Carleton Place',
      'Almonte',
      'Beckwith',
      'Pakenham',
      'Mississippi Mills',
      'Appleton',
      'Smiths Falls',
      'Stittsville',
    ],
    useCases: [
      'Bedroom-community moves to and from Kanata or the Ottawa west end',
      'Dump runs to the Carp landfill',
      'Snowmobile and ATV transport to Calabogie',
      'Mississippi Lake cottage furniture and dock moves',
      'Almonte heritage-property renovation hauls',
    ],
    popularTypes: [
      { slug: 'utility', label: 'Utility', emoji: '🛻', example: 'Daily hauls' },
      { slug: 'enclosed', label: 'Enclosed', emoji: '📦', example: 'Weatherproof gear' },
      { slug: 'dump', label: 'Dump', emoji: '🪨', example: 'Renos & landscaping' },
      { slug: 'snowmobile', label: 'Snowmobile', emoji: '❄️', example: 'Sleds to Calabogie' },
    ],
    whyHere: [
      'Carleton Place is the closest TowShare market to west-end Ottawa — many Kanata renters book Carleton Place trailers and save 15–25% versus city listings.',
      'High concentration of owner-occupied homes with driveway space means lots of larger trailers (18′+ enclosed, 14′ dumps).',
      'Highway 7 access makes it convenient for renters in Stittsville, Almonte, and Smiths Falls.',
    ],
    faqs: [
      {
        q: 'Can I rent a trailer in Carleton Place from Kanata?',
        a: 'Absolutely — many TowShare renters from Kanata, Stittsville, and Bridlewood book Carleton Place trailers because they\'re a 20-minute drive away and noticeably cheaper than equivalent city listings.',
      },
      {
        q: 'Where can I rent a snowmobile trailer near Carleton Place?',
        a: 'Snowmobile trailers cluster in Carleton Place, Almonte, and Pakenham. Most are listed November through March at /search?type=snowmobile.',
      },
      ...SHARED_FAQS('Carleton Place'),
    ],
  },

  peterborough: {
    slug: 'peterborough',
    name: 'Peterborough',
    region: 'Ontario',
    nickname: 'the gateway to the Kawarthas',
    lat: 44.3091,
    lng: -78.3197,
    population: 83651,
    tagline:
      'Cottage-country trailer rentals — boat, snowmobile, motorcycle, and utility trailers across Peterborough, Lakefield, Bridgenorth, and the Kawartha Lakes.',
    neighbourhoods: [
      'Downtown Peterborough',
      'East City',
      'Lakefield',
      'Bridgenorth',
      'Ennismore',
      'Selwyn',
      'Buckhorn',
      'Apsley',
      'Curve Lake',
      'Chemong Lake',
      'Stoney Lake',
    ],
    useCases: [
      'Spring and fall boat haul-outs across Chemong, Buckhorn, and Stoney Lake',
      'Snowmobile transport to Haliburton and the Kawartha Highlands',
      'Cottage furniture, dock, and propane-tank moves',
      'ATV and dirt-bike transport to Ganaraska and the Northumberland Forest',
      'Trent University move-in and move-out weekends',
    ],
    popularTypes: [
      { slug: 'utility', label: 'Utility', emoji: '🛻', example: 'Cottage moves' },
      { slug: 'enclosed', label: 'Enclosed', emoji: '📦', example: 'Weatherproof gear' },
      { slug: 'snowmobile', label: 'Snowmobile', emoji: '❄️', example: 'Sleds to Haliburton' },
      { slug: 'motorcycle', label: 'Motorcycle', emoji: '🏍️', example: 'Bikes to Ganaraska' },
    ],
    whyHere: [
      'Peterborough sits at the heart of cottage country — TowShare owners here list more boat- and recreation-capable trailers per capita than any other market.',
      'Lakefront communities around Chemong, Buckhorn, and Stoney Lake have driveway space for 20′+ trailers that simply don\'t exist at city rental yards.',
      'Trent University moves create twice-yearly demand spikes — book 2–3 weeks ahead in May and September.',
    ],
    faqs: [
      {
        q: 'Can I rent a boat trailer in Peterborough?',
        a: 'Yes — boat-capable utility and flatbed trailers are TowShare Peterborough\'s most popular spring/fall categories. Pickups cluster in Bridgenorth, Lakefield, Ennismore, and Selwyn.',
      },
      {
        q: 'Where can I rent a snowmobile trailer in the Kawarthas?',
        a: 'Snowmobile trailers are available across Peterborough, Lakefield, Buckhorn, and Apsley from November through March. Filter by /search?type=snowmobile to see what\'s nearest to your pickup point.',
      },
      ...SHARED_FAQS('Peterborough'),
    ],
  },
};

export const CITY_LIST: City[] = Object.values(CITIES);
